<template>
  <div v-show="isCustomFieldValid">
    <label
      v-if="!props.hideLabel"
      class="input-label tw-flex tw-items-center"
      :for="`${customField?.name}`"
    >
      {{ customField?.name }}
      <span
        v-if="props.customField.required"
        class="tw-ml-0.5 tw-text-sm tw-text-red"
      >
        *
      </span>
      <help-text v-if="!!customField.description" class="tw-mb-0.5 tw-ml-1">
        {{ customField.description }}
      </help-text>
    </label>
    <q-input
      :id="`${customField?.name}`"
      v-model="localVal"
      :for="`${customField?.name}`"
      :name="`${customField?.name}`"
      dense
      autofill="off"
      outlined
      :readonly="props.readonly"
      class="input-xs tw-w-full tw-bg-white"
      :class="{
        'form-input--error': !!errorMessage && showErrorMessage,
      }"
      type="email"
      :placeholder="props.readonly ? '' : 'Enter an email address'"
      @blur="onBlur"
      @keydown.enter="handleEnter"
      @keydown.stop.escape="handleEscape"
    />
    <span
      v-if="!!errorMessage && showErrorMessage"
      class="form-input__error-message"
    >
      {{ errorMessage }}
    </span>
  </div>
</template>

<script setup lang="ts">
import isEmail from 'validator/es/lib/isEmail';
import { ref, computed, watch } from 'vue';
import HelpText from '../design-system/misc/HelpText.vue';
import { CustomField, CustomFieldTextSubType, CustomFieldType } from '@/api';

interface Props {
  customField: CustomField & { required?: boolean };
  value?: string;
  hideLabel?: boolean;
  readonly?: boolean;
}
interface Emits {
  (e: 'value-updated', value: string | null): void;
  (e: 'enter'): void;
  (e: 'escape'): void;
}

const props = withDefaults(defineProps<Props>(), {});
const emit = defineEmits<Emits>();

const isCustomFieldValid = computed(() => {
  return (
    props.customField.type === CustomFieldType.TEXT &&
    props.customField.subType === CustomFieldTextSubType.EMAIL
  );
});

const localVal = ref<string>(props.value || '');
watch(
  () => props.value,
  () => {
    localVal.value = props.value || '';
  }
);

const showErrorMessage = ref(false);

const errorMessage = computed(() => {
  if (!localVal.value && props.customField.required) {
    return 'This field is required';
  }
  if (!localVal.value) {
    return '';
  }
  if (!isEmail(localVal.value)) return 'Invalid email address';
  return '';
});

watch(localVal, () => {
  showErrorMessage.value = false;
});

function onBlur() {
  if (
    props.customField.required &&
    (!localVal.value || !isEmail(localVal.value))
  ) {
    localVal.value = props.value || '';
    return;
  }
  onValueChange();
}

function onValueChange() {
  if (errorMessage.value) {
    showErrorMessage.value = true;
    return;
  }
  if (localVal.value === props.value || (!localVal.value && !props.value))
    return;
  emit('value-updated', localVal.value);
}

const isValid = computed(() => {
  return !errorMessage.value;
});

function validate() {
  showErrorMessage.value = !isValid.value;
}

function removeError() {
  showErrorMessage.value = false;
}

function handleEnter() {
  // Emit the current value before emitting enter
  if (localVal.value !== props.value) {
    emit('value-updated', localVal.value);
  }
  emit('enter');
}

function handleEscape() {
  localVal.value = props.value || '';
  emit('escape');
}

defineExpose({
  validate,
  removeError,
  isValid,
});
</script>
